import { Icon } from "@mdi/react";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { mdiGrid, mdiGridLarge } from "@mdi/js";
import { useAxisGridlineStore } from "stores/axisGridline/useAxisGridlineStore";
import styled from "styled-components/macro";

import { ChevronDown } from "components/icons";
import SegmentedToggle, { SegmentedVariant } from "components/shared/SegmentedToggle";

import { useScreenshotFormContext } from "../contexts/ScreenshotFormContext";
import { useScreenshotCalculatedDefaults, useScreenshotContext } from "../hooks";
import ScreenshotSettingItem from "./ScreenshotSettingItem";
import ScreenshotSettingItemInput from "./ScreenshotSettingItemInput";

export interface AxisEntry {
  label: string;
  isPrimary: boolean;
  settingsKey: string;
  gridMajorKey: string;
  gridMinorKey: string;
}

interface AxisSettingsListProps {
  xAxes: AxisEntry[];
  yAxes: AxisEntry[];
  showAxisTitle: boolean;
  // The P/S (primary/secondary) badge distinguishes multi-product axes; charts
  // whose two y-axes aren't products (e.g. Rig/Frac Activity) hide it.
  showPrimaryBadge?: boolean;
}

function AxisSettingsList({
  xAxes,
  yAxes,
  showAxisTitle,
  showPrimaryBadge = true
}: AxisSettingsListProps) {
  const [selectedType, setSelectedType] = useState<"x" | "y">("y");
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const { values } = useScreenshotFormContext();
  const defaults = useScreenshotCalculatedDefaults();
  const { widget } = useScreenshotContext();
  const { setGridline, storeGridlines } = useAxisGridlineStore(
    useShallow((s) => ({
      setGridline: s.setGridline,
      storeGridlines: s.gridlines[widget?.widgetId ?? ""] ?? {}
    }))
  );

  const currentAxes = selectedType === "x" ? xAxes : yAxes;
  const axisLetter = selectedType === "x" ? "X" : "Y";

  const isMergedProductMode = !!(values.mergeSameUnitAxes && values.showProductLegend);

  const getAxisDisplayLabel = (axis: AxisEntry): string => {
    if (isMergedProductMode && selectedType === "x") return axis.label;
    const edited = values[axis.settingsKey];
    return typeof edited === "string" && edited.trim() !== "" ? edited : axis.label;
  };

  const segmentOptions = [
    { label: `X Axes (${xAxes.length})`, value: "x" },
    { label: `Y Axes (${yAxes.length})`, value: "y" }
  ];

  // The gridline store is the sole authority, then the calculated default — the form
  // context is deliberately not a tier here. ScreenshotActivity strips every gridline key
  // out of formValues and re-spreads the store on each store change, in both modes, so a
  // `values[key]` tier could only ever repeat the store value (already read above) or the
  // default (read below). Reading it would also put toggleGridline's default comparison
  // out of step with this one.
  const getGridlineValue = (key: string): boolean =>
    (storeGridlines[key] ?? defaults[key] ?? false) as boolean;

  // A value equal to the calculated default is not an override — store null so the key
  // is dropped and the default keeps tracking the axis scale. Without this a toggle
  // round-trip pins the default as a preference, and the pinned value then outlives the
  // log mode that produced it (EVA-84).
  //
  // Same intent as ScreenshotSettingsForm's reset-to-default handling, but deliberately
  // not the same rule: that one uses checkKeyValuePairMatch, which only counts a value as
  // the default when the key exists in the defaults map. The MPC secondary-axis keys that
  // only this control writes (gridShowY2Lines, gridShowX2MinorLines, …) are never in that
  // map, so an absent default has to mean `false` here — which is exactly how
  // getChartScreenshotOptions renders them (`axisSettings[key] ?? false`). If that
  // per-axis fallback ever changes — secondary axes inheriting from the primary, say —
  // this `?? false` has to change with it, or a cleared key starts reading as "on".
  const toggleGridline = (key: string) => {
    if (!widget?.widgetId) return;
    const next = !getGridlineValue(key);
    const isDefault = next === ((defaults[key] ?? false) as boolean);
    setGridline(widget.widgetId, key, isDefault ? null : next);
  };

  return (
    <Container>
      <SegmentedToggle
        value={selectedType}
        options={segmentOptions}
        onChange={(v) => {
          setSelectedType(v as "x" | "y");
          setExpandedIndices(new Set());
        }}
        variant={SegmentedVariant.Base}
        block
      />
      <AxisList>
        {currentAxes.map((axis, idx) => {
          const isExpanded = expandedIndices.has(idx);
          const majorActive = getGridlineValue(axis.gridMajorKey);
          const minorActive = getGridlineValue(axis.gridMinorKey);
          const handleToggle = () =>
            setExpandedIndices((prev) => {
              const next = new Set(prev);
              next.has(idx) ? next.delete(idx) : next.add(idx);
              return next;
            });
          return (
            <AxisItemContainer key={axis.settingsKey} $isExpanded={isExpanded}>
              <AxisItemHeader
                role="button"
                tabIndex={0}
                onClick={handleToggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggle();
                  }
                }}>
                <BadgeRow>
                  <AxisTypeBadge>{axisLetter}</AxisTypeBadge>
                  {showPrimaryBadge && selectedType === "y" && (
                    <ProductBadge $isPrimary={axis.isPrimary}>
                      {axis.isPrimary ? "P" : "S"}
                    </ProductBadge>
                  )}
                  <AxisItemLabel title={getAxisDisplayLabel(axis)}>
                    {getAxisDisplayLabel(axis)}
                  </AxisItemLabel>
                </BadgeRow>
                <ChevronWrapper $isOpen={isExpanded}>
                  <ChevronDown size={16} />
                </ChevronWrapper>
              </AxisItemHeader>
              {isExpanded && (
                <AxisItemBody>
                  {showAxisTitle && (
                    <ScreenshotSettingItem
                      name={axis.settingsKey}
                      label="Axis Title"
                      labelWidth="70px">
                      <ScreenshotSettingItemInput type="text" />
                    </ScreenshotSettingItem>
                  )}
                  <GridlinesRow>
                    <GridlinesLabel>Gridlines</GridlinesLabel>
                    <GridlinesButtons>
                      <GridlineButton
                        $active={majorActive}
                        onClick={() => toggleGridline(axis.gridMajorKey)}>
                        <Icon path={mdiGridLarge} size={"16px"} />
                        Major
                      </GridlineButton>
                      <GridlineButton
                        $active={minorActive}
                        onClick={() => toggleGridline(axis.gridMinorKey)}>
                        <Icon path={mdiGrid} size={"16px"} />
                        Minor
                      </GridlineButton>
                    </GridlinesButtons>
                  </GridlinesRow>
                </AxisItemBody>
              )}
            </AxisItemContainer>
          );
        })}
      </AxisList>
    </Container>
  );
}

export default AxisSettingsList;

const Container = styled.div`
  width: 100%;
`;

const AxisList = styled.div`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AxisItemContainer = styled.div<{ $isExpanded: boolean }>`
  border: 1px solid #d9e1e2;
  border-radius: 4px;
  overflow: hidden;
  background: white;
`;

const AxisItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--color-primary-06);
  }
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const AxisTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #222;
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const ProductBadge = styled.span<{ $isPrimary: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $isPrimary }) =>
    $isPrimary ? "var(--color-primary)" : "var(--color-primary-hover)"};
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const AxisItemLabel = styled.span`
  font-size: 1.3rem;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronWrapper = styled.div<{ $isOpen: boolean }>`
  display: flex;
  transition: transform var(--duration-short) ease;
  transform: rotate(${({ $isOpen }) => ($isOpen ? "0deg" : "-90deg")});
  color: var(--color-text-40);
  flex-shrink: 0;
`;

const AxisItemBody = styled.div`
  padding: 8px 10px 10px;
  border-top: 1px solid #d9e1e2;
  background: var(--color-background-complementary);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GridlinesRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr;
  align-items: center;
  padding: 0 2px;
`;

const GridlinesLabel = styled.span`
  font-size: 1.3rem;
  color: var(--color-text-50);
  min-width: 90px;
`;

const GridlinesButtons = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
`;

const GridlineButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 4px;
  padding: 3px 12px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? "var(--color-primary)" : "#d9d9d9")};
  background: ${({ $active }) => ($active ? "var(--color-primary-10)" : "white")};
  color: ${({ $active }) => ($active ? "var(--color-primary)" : "var(--color-text-50)")};
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1.4;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
`;
